import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useState, useEffect } from 'react';
import { Save, Globe, Check } from 'lucide-react';
import { axiosInstance as axios } from '@/lib/api';
import { AppLayout } from '@/components/layout/app-layout';

export function AdminPlatformSettingsPage() {
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get('/api/platform-settings')
      .then((res) => {
        setApiBaseUrl(res.data.data.apiBaseUrl ?? '');
      })
      .catch(() => setError('Error cargando configuración'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      new URL(apiBaseUrl);
      await axios.put('/api/platform-settings', { apiBaseUrl });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      if (err instanceof TypeError) {
        setError('URL inválida. Debe incluir protocolo (https://)');
      } else {
        setError(err.response?.data?.message ?? 'Error guardando configuración');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <AppLayout variante="plataforma">
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div>
          <div className="mb-3">
            <EtiquetaDePagina tono="neutro">Plataforma</EtiquetaDePagina>
          </div>
          <h1 className={CLASE_TITULO_DE_PAGINA}>
            <Globe className="w-6 h-6" />
            Configuración de Plataforma
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Configuraciones globales que afectan a toda la plataforma.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Base URL de la API
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              URL pública donde los clientes externos acceden a la API. Se usa en snippets de
              código, documentación Swagger y la página de integración.
            </p>
            <input
              type="url"
              value={apiBaseUrl}
              onChange={(e) => setApiBaseUrl(e.target.value)}
              placeholder="https://api.midominio.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Ejemplo: https://api.miempresa.com</p>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {saving ? 'Guardando...' : saved ? 'Guardado' : 'Guardar'}
            </button>
            {saved && (
              <span className="text-sm text-green-600 dark:text-green-400">
                Configuración actualizada correctamente.
              </span>
            )}
          </div>
        </div>

        {apiBaseUrl && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Preview — Así se verán los snippets:
            </h3>
            <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto">
              {`curl -H "X-API-Key: pk_live_..." ${apiBaseUrl}/api/v1/price/SKU-001`}
            </pre>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
